<script setup>
import { ref, computed, getCurrentInstance, onMounted } from 'vue';
import { useForm, router } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import Button from 'primevue/button';
import Card from 'primevue/card';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import Message from 'primevue/message';
import Toast from 'primevue/toast';
import SiteFormInputs from '@/components/sites/SiteFormInputs.vue';
import PricingTable from '@/components/sites/PricingTable.vue';
import HistoryTable from '@/components/sites/HistoryTable.vue';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();


// Helper function to check if current user can edit pricing for this seller
const canEditPricing = (seller) => {
    if (seller && typeof seller === 'object' && seller.linkarme_seller !== undefined) {
        return Boolean(seller.linkarme_seller);
    }
    return false;
};

const props = defineProps({
    site: Object,
    coins: Object,
    categories: Array,
    languages: Array,
    countries: Array,
    sellers: Array,
    teams: Array,
    types: Array,
    note: Object,
    exchangeData: Object,
    sellerPricingData: Object,
    showcasePrice: Number,
    showcasePriceCoin: String,
    showcaseSellerName: String,
    showcaseTypeName: String,
    currencies: Array,
    exchangeValues: Array,
    cartsWithSite: Array,
    ordersWithSite: Array,
    liveLinksWithSite: Array,
    offerHistory: Array,
});

// Tab management
const activeIndex = ref(0);

// Dialog for confirmation
const confirmDialog = ref(false);
const confirmPricingDialog = ref(false);
const confirmRemovePricingDialog = ref(false);
const sellerToRemove = ref(null);
const savingOfferId = ref(null);
const expandedTables = ref(new Set());
const isAddingPricingTable = ref(false);
const isRemovingPricingTable = ref(false);
const selectedSellerForNewPricing = ref(null);

// Store original pricing values for comparison (will be updated after form initialization)
const originalOffers = ref([]);
const externalEditModes = ref(new Map());

// Helper function to process offers data
const processOffers = (siteOffers, types) => {
    // Helper function to format value for display (expects value in cents from backend)
    const formatValueForDisplay = (valueInCents) => {
        if (!valueInCents || valueInCents === 0) return '';
        const valueInDecimals = (valueInCents / 100).toFixed(2);
        return valueInDecimals.replace('.', ',');
    };

    // Group offers by seller to create one pricing table per seller
    const offersBySeller = {};

    siteOffers.forEach(offer => {
        const sellerId = offer.seller_id;
        if (!offersBySeller[sellerId]) {
            offersBySeller[sellerId] = {
                id: offer.id, // Use the actual offer ID (will be updated with first offer)
                seller_id: sellerId,
                seller: offer.seller,
                site_id: offer.site_id,
                offers: []
            };
        }
        offersBySeller[sellerId].offers.push(offer);
    });

    // Convert grouped offers into pricing tables
    return Object.values(offersBySeller).map(sellerGroup => {
        // Create a complete types array for this seller
        const allTypes = types.map(_t => {
            const type = JSON.parse(JSON.stringify(_t));
            const isDefault = type.name === 'Default';

            // Find the offer for this type
            const offerForType = sellerGroup.offers.find(offer =>
                offer.type_id === type.id || (offer.type && offer.type.id === type.id)
            );

            if (offerForType) {
                // This type has an offer - use its data
                const isLinkarmeOffer = sellerGroup.seller?.linkarme_seller === true || sellerGroup.seller?.linkarme_seller === 1;
                const hasData = (offerForType.cost && offerForType.cost > 0) || (offerForType.sale && offerForType.sale > 0);

                const shouldBeAvailable = isLinkarmeOffer ? hasData : true;

                return {
                    ...type,
                    isDefault: isDefault,
                    available: shouldBeAvailable,
                    cost: formatValueForDisplay(offerForType.cost || 0),
                    cost_coin: offerForType.cost_coin || 'BRL',
                    sale: formatValueForDisplay(offerForType.sale || 0),
                    sale_coin: offerForType.sale_coin || 'BRL',
                    accepted_anchors: offerForType.accepted_anchors ?? 0,
                    offer_id: offerForType.id
                }
            } else if (isDefault) {
                // Default type but no offer - show as unavailable and empty
                return {
                    ...type,
                    isDefault: true,
                    available: false,
                    cost: '',
                    cost_coin: 'BRL',
                    sale: '',
                    sale_coin: 'BRL',
                    accepted_anchors: 0,
                    offer_id: null
                }
            } else {
                // Other type without offer - mark as unavailable
                return {
                    ...type,
                    isDefault: false,
                    available: false,
                    cost: '',
                    cost_coin: 'BRL',
                    sale: '',
                    sale_coin: 'BRL',
                    accepted_anchors: 0,
                    offer_id: null
                }
            }
        });

        // Sort to put Default first
        allTypes.sort((a, b) => {
            if (a.name === 'Default') return -1;
            if (b.name === 'Default') return 1;
            return 0;
        });

        return {
            ...sellerGroup,
            types: allTypes
        };
    });
};

// Form for site data
const form = useForm({
    id: props.site.id,
    url: props.site.url,
    category_id: props.site.category_id,
    da: props.site.da,
    dr: props.site.dr,
    traffic: props.site.traffic,
    language_id: props.site.language_id,
    country_id: props.site.country_id,
    link_type: props.site.link_type,
    branded_content: props.site.branded_content ?? false,

    gambling: props.site.gambling ?? false,
    sponsor: props.site.sponsor ?? false,
    menu: props.site.menu ?? false,
    banner: props.site.banner ?? false,

    last_posted: props.site.last_posted,
    wpp_group: props.site.wpp_group,
    validation_date: props.site.validation_date,
    mark_as_published: props.site.mark_as_published ?? false,
    seller_id: props.site.seller_id,
    team_id: props.site.team_id,

    obs: props.site.obs,
    type_observation: props.site.type_observation,

    owner_name: props.site.owner_name,
    owner_email: props.site.owner_email,
    owner_phone: props.site.owner_phone,
    bank: props.site.bank,
    pix: props.site.pix,
    phone: props.site.phone,
    paypal: props.site.paypal,
    instagram: props.site.instagram,
    facebook: props.site.facebook,

    owner_role: props.site.owner_role,
    promo: props.site.promo ?? false,
    restrict: props.site.restrict ?? false,
    restrict_detail: props.site.restrict_detail,
    archive_article: props.site.archive_article ?? false,
    archive_due: props.site.archive_due,
    links: props.site.links,
    embed: props.site.embed ?? false,
    images: props.site.images,
    example_article: props.site.example_article,
    rules: props.site.rules,
    google_news: props.site.google_news ?? false,
    global_account: props.site.global_account,

    // Entry Information fields
    inserted_at: props.site.inserted_at,
    last_updated_at: props.site.last_updated_at,
    created_at: props.site.created_at,
    updated_at: props.site.updated_at,

    offers: processOffers(props.site.offers, props.types),
});

// Form for note
const noteForm = useForm({
    text: props.note?.text ?? '',
});

// Options
const linkTypeOptions = [
    { label: 'DOFOLLOW', value: 'DOFOLLOW' },
    { label: 'NOFOLLOW', value: 'NOFOLLOW' },
    { label: 'SPONSOR', value: 'SPONSOR' },
];

const linksOptions = [
    { label: '3', value: 3 },
    { label: '5', value: 5 },
    { label: $t('No limit'), value: null },
];

const imagesOptions = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 },
];

// Format currency inputs with two decimal places
const formatCurrency = (value) => {
    if (!value || value === '') return '';

    // Remove all non-numeric characters except dots and commas
    let cleanValue = value.toString().replace(/[^\d.,]/g, '');
    cleanValue = cleanValue.replace(',', '.');

    const numValue = parseFloat(cleanValue);
    if (isNaN(numValue)) return '';

    return numValue.toFixed(2).replace('.', ',');
};

// Currency options
const currencyOptions = [
    { label: 'BRL', value: 'BRL' },
    { label: 'EUR', value: 'EUR' },
    { label: 'USD', value: 'USD' },
    { label: 'GBP', value: 'GBP' }
];

// Helper function to convert currency using exchange rates
const convertCurrency = (amount, fromCurrency, toCurrency) => {
    if (!props.exchangeData || fromCurrency === toCurrency) {
        return amount;
    }

    const fromCurrencyUpper = fromCurrency?.toUpperCase();
    const toCurrencyUpper = toCurrency?.toUpperCase();

    const fromExchange = props.exchangeData[fromCurrencyUpper];
    const toExchange = props.exchangeData[toCurrencyUpper];

    if (!fromExchange || !toExchange) {
        return amount;
    }

    const fromRate = (fromExchange.value || 100) / 100;
    const toRate = (toExchange.value || 100) / 100;

    // Convert amount to USD, then to target currency
    const usdAmount = amount / fromRate;
    const convertedAmount = usdAmount * toRate;

    return convertedAmount;
};

const isExternalEditMode = (offerId) => {
    return externalEditModes.value.get(offerId) || false;
};

const toggleExternalEditMode = (offerId) => {
    const current = externalEditModes.value.get(offerId) || false;
    externalEditModes.value.set(offerId, !current);
    externalEditModes.value = new Map(externalEditModes.value);
};

// Calculate suggested price based on cost, sale currency and exchange data
const getSuggestedPrice = (typeData) => {
    if (!props.exchangeData || !typeData || !typeData.cost || !typeData.sale_coin) {
        return '-';
    }

    const costString = typeData.cost?.toString() || '';
    if (!costString.trim()) return '-';

    // Convert "215,00" to 215.00
    const cleanCost = costString.replace(',', '.');
    const costValue = parseFloat(cleanCost);

    if (isNaN(costValue) || costValue <= 0) return '-';

    // Get cost currency (default to BRL if not specified)
    const costCurrency = typeData.cost_coin || 'BRL';
    const saleCurrency = typeData.sale_coin?.toUpperCase();

    // Convert cost from cost_coin to sale_coin
    const convertedCost = convertCurrency(costValue, costCurrency, saleCurrency);

    // Get exchange data for the sale currency
    const exchangeInfo = props.exchangeData[saleCurrency];

    if (!exchangeInfo) return '-';

    // Get exchange parameters
    const fixedAddition = (exchangeInfo.fixed_addition || 0) / 100;
    const percentageAddition = (exchangeInfo.percentage_addition || 0) / 10000;
    const applyPercentageToTotal = exchangeInfo.apply_percentage_to_total || false;

    let suggestedPrice;

    if (applyPercentageToTotal) {
        const baseAmount = convertedCost + fixedAddition;
        suggestedPrice = baseAmount * (1 + percentageAddition);
    } else {
        const percentageAmount = convertedCost * percentageAddition;
        suggestedPrice = convertedCost + percentageAmount + fixedAddition;
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: saleCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(suggestedPrice);
};

// Get pricing changes for confirmation dialog
const getPricingChanges = (offerId = null) => {
    const changes = [];

    // Helper to convert string "215,00" or "215.00" to float
    const parseDecimalValue = (value) => {
        if (!value || value === '') return 0;
        const cleanValue = value.toString().replace(',', '.');
        return parseFloat(cleanValue) || 0;
    };

    const offersToCheck = offerId
        ? form.offers.filter(o => o.id === offerId)
        : form.offers;

    offersToCheck.forEach((currentOffer, offerIndex) => {
        const originalOffer = originalOffers.value.find(o => o.id === currentOffer.id);
        if (!originalOffer || (!canEditPricing(currentOffer.seller) && !isExternalEditMode(currentOffer.id))) return;

        // Show all available types (including Default which is now in the types array)
        currentOffer.types.forEach(currentType => {
            // Only show available types
            if (!currentType.available) return;

            const originalType = originalOffer.types.find(t => t.id === currentType.id);

            if (!originalType) {
                // New type added
                changes.push({
                    type: currentType.name,
                    oldCost: 0,
                    newCost: parseDecimalValue(currentType.cost),
                    costCoin: currentType.cost_coin,
                    oldCostCoin: 'BRL',
                    oldSale: 0,
                    newSale: parseDecimalValue(currentType.sale),
                    saleCoin: currentType.sale_coin,
                    oldSaleCoin: 'BRL',
                    costChanged: true,
                    saleChanged: true,
                    available: true,
                    suggested: getSuggestedPrice(currentType),
                    sellerName: currentOffer.seller?.name || 'Unknown Seller',
                    isLinkarme: canEditPricing(currentOffer.seller)
                });
            } else {
                // Type exists - show it regardless of changes
                const currentCost = parseDecimalValue(currentType.cost);
                const originalCost = parseDecimalValue(originalType.cost);
                const currentSale = parseDecimalValue(currentType.sale);
                const originalSale = parseDecimalValue(originalType.sale);

                const costChanged = currentCost !== originalCost;
                const saleChanged = currentSale !== originalSale;
                const costCoinChanged = currentType.cost_coin !== (originalType.cost_coin || 'BRL');
                const saleCoinChanged = currentType.sale_coin !== (originalType.sale_coin || 'BRL');
                const availabilityChanged = currentType.available !== originalType.available;

                changes.push({
                    type: currentType.name,
                    oldCost: originalCost,
                    newCost: currentCost,
                    costCoin: currentType.cost_coin,
                    oldCostCoin: originalType.cost_coin || 'BRL',
                    oldSale: originalSale,
                    newSale: currentSale,
                    saleCoin: currentType.sale_coin,
                    oldSaleCoin: originalType.sale_coin || 'BRL',
                    costChanged: costChanged || costCoinChanged,
                    saleChanged: saleChanged || saleCoinChanged,
                    available: currentType.available,
                    suggested: getSuggestedPrice(currentType),
                    sellerName: currentOffer.seller?.name || 'Unknown Seller',
                    isLinkarme: canEditPricing(currentOffer.seller)
                });
            }
        });
    });

    return changes;
};

const editableOffers = computed(() => {
    return (form.offers || []).filter(offer => canEditPricing(offer.seller));
});

// Group pricing changes by seller name
const pricingChangesBySeller = computed(() => {
    const changes = getPricingChanges(savingOfferId.value);
    const grouped = {};

    changes.forEach(change => {
        const sellerName = change.sellerName;
        if (!grouped[sellerName]) {
            grouped[sellerName] = [];
        }
        grouped[sellerName].push(change);
    });

    return grouped;
});

const showPricingConfirmation = (offerId = null) => {
    savingOfferId.value = offerId;
    // Validate pricing data first
    const validationErrors = validatePricingData(offerId);

    if (validationErrors.length > 0) {
        // Group errors by seller
        const errorsBySeller = {};
        validationErrors.forEach(error => {
            if (!errorsBySeller[error.seller]) {
                errorsBySeller[error.seller] = [];
            }
            errorsBySeller[error.seller].push(error.message);
        });

        // Format error message
        let errorMessage = 'Please fix the following errors:\n\n';
        Object.keys(errorsBySeller).forEach(seller => {
            errorMessage += `${seller}:\n`;
            errorsBySeller[seller].forEach(msg => {
                errorMessage += `  • ${msg}\n`;
            });
            errorMessage += '\n';
        });

        toast.add({
            severity: 'error',
            summary: 'Validation Error',
            detail: errorMessage,
            life: 8000
        });
        return;
    }

    const changes = getPricingChanges(offerId);
    if (changes.length === 0) {
        toast.add({
            severity: 'warn',
            summary: 'Warning',
            detail: 'No available pricing types to save',
            life: 3000
        });
        return;
    }
    confirmPricingDialog.value = true;
};

const confirmAndSavePricing = () => {
    confirmPricingDialog.value = false;
    submitPricing(savingOfferId.value);
};

const submit = () => {
    form.transform(data => {
        const tdata = { ...data };

        // Remove offers data and set types as empty array for non-pricing updates
        // Pricing updates are handled by submitPricing() function
        // Backend requires 'types' to be present (even if empty) due to 'present' validation rule
        if (tdata.offers) {
            delete tdata.offers;
        }

        // Set types as empty array to satisfy backend validation but indicate no pricing update
        tdata.types = [];

        if (tdata.offer_id) {
            delete tdata.offer_id;
        }

        return tdata;
    }).put(route('sites.update', props.site.id), {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Site updated successfully',
                life: 3000
            });
        },
        onError: (error) => {
            console.log(error);
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update site',
                life: 5000
            });
        }
    });
};

const validatePricingData = (offerId = null) => {
    const errors = [];

    const parseDecimalValue = (value) => {
        if (!value || value === '') return 0;
        const cleanValue = value.toString().replace(',', '.');
        return parseFloat(cleanValue) || 0;
    };

    const offersToValidate = offerId
        ? form.offers.filter(o => o.id === offerId)
        : form.offers;

    offersToValidate.forEach((offer) => {
        if (!canEditPricing(offer.seller) && !isExternalEditMode(offer.id)) return;

        const sellerName = offer.seller?.name || 'Unknown Seller';

        offer.types.forEach(type => {
            if (!type.available) return;

            const costValue = parseDecimalValue(type.cost);
            const saleValue = parseDecimalValue(type.sale);

            if (costValue <= 0) {
                errors.push({
                    seller: sellerName,
                    type: type.name,
                    field: 'Cost',
                    message: `${type.name}: Cost must be greater than 0`
                });
            }

            if (canEditPricing(offer.seller) && saleValue <= 0) {
                errors.push({
                    seller: sellerName,
                    type: type.name,
                    field: 'Sale',
                    message: `${type.name}: Sale must be greater than 0`
                });
            }
        });
    });

    return errors;
};

const submitPricing = async (offerId = null) => {
    const validationErrors = validatePricingData(offerId);

    if (validationErrors.length > 0) {
        toast.add({
            severity: 'error',
            summary: 'Validation Error',
            detail: 'Please ensure all costs and sales are greater than 0',
            life: 5000
        });
        return;
    }

    let editableOffers = form.offers.filter(offer => canEditPricing(offer.seller) || isExternalEditMode(offer.id));
    if (offerId) {
        editableOffers = editableOffers.filter(offer => offer.id === offerId);
    }

    if (editableOffers.length === 0) {
        toast.add({
            severity: 'warn',
            summary: 'Warning',
            detail: 'No editable offers found',
            life: 3000
        });
        return;
    }

    try {
        for (let i = 0; i < editableOffers.length; i++) {
            const offer = editableOffers[i];

            const requestData = {
                id: form.id,
                url: form.url,
                category_id: form.category_id,
                da: form.da,
                dr: form.dr,
                traffic: form.traffic,
                language_id: form.language_id,
                country_id: form.country_id,
                link_type: form.link_type,
                branded_content: form.branded_content,
                gambling: form.gambling,
                sponsor: form.sponsor,
                menu: form.menu,
                banner: form.banner,
                last_posted: form.last_posted,
                seller_id: form.seller_id,
                team_id: form.team_id,
                obs: form.obs,
                owner_name: form.owner_name,
                owner_email: form.owner_email,
                owner_phone: form.owner_phone,
                bank: form.bank,
                pix: form.pix,
                phone: form.phone,
                paypal: form.paypal,
                instagram: form.instagram,
                facebook: form.facebook,
                owner_role: form.owner_role,
                promo: form.promo,
                restrict: form.restrict,
                restrict_detail: form.restrict_detail,
                archive_article: form.archive_article,
                archive_due: form.archive_due,
                links: form.links,
                embed: form.embed,
                images: form.images,
                example_article: form.example_article,
                rules: form.rules,
                google_news: form.google_news,
                global_account: form.global_account,
                type_observation: form.type_observation,

                // New fields
                wpp_group: form.wpp_group,
                validation_date: form.validation_date,
                mark_as_published: form.mark_as_published,
                // Pricing data for this specific offer
                offer_id: parseInt(offer.id, 10),
                types: offer.types.map(type => ({
                    id: type.id,
                    available: type.available,
                    cost: type.cost || 0,
                    cost_coin: type.cost_coin,
                    sale: type.sale || 0,
                    sale_coin: type.sale_coin,
                    accepted_anchors: type.accepted_anchors ?? 0,
                    offer_id: type.offer_id ? parseInt(type.offer_id, 10) : null
                }))
            };

            await new Promise((resolve, reject) => {
                router.put(route('sites.update', props.site.id), requestData, {
                    preserveScroll: true,
                    preserveState: true,
                    onSuccess: () => {
                        resolve();
                    },
                    onError: (errors) => {
                        const errorMessage = errors.message || Object.values(errors)[0] || 'Unknown error';
                        reject(new Error(`Failed to update ${offer.seller?.name}: ${errorMessage}`));
                    }
                });
            });
        }

        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: `Successfully updated pricing for ${editableOffers.length} seller(s)`,
            life: 3000
        });
        const savedOfferIds = editableOffers.map(o => o.id);
        savedOfferIds.forEach(savedId => {
            const idx = originalOffers.value.findIndex(o => o.id === savedId);
            const currentOffer = form.offers.find(o => o.id === savedId);
            if (idx !== -1 && currentOffer) {
                originalOffers.value[idx] = JSON.parse(JSON.stringify(currentOffer));
            }
        });

        // Force reload to get fresh data from backend
        router.reload({
            only: ['site'],
            onSuccess: () => {
                setTimeout(() => {
                    savedOfferIds.forEach(savedId => {
                        const idx = originalOffers.value.findIndex(o => o.id === savedId);
                        const currentOffer = form.offers.find(o => o.id === savedId);
                        if (idx !== -1 && currentOffer) {
                            originalOffers.value[idx] = JSON.parse(JSON.stringify(currentOffer));
                        }
                    });
                }, 100);
            }
        });
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update pricing',
            life: 5000
        });
    }
};

const submitNote = () => {
    noteForm.put(route('notes.update', props.site.id), {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Note updated successfully',
                life: 3000
            });
        }
    });
};

const approve = () => {
    form.transform(data => {
        const tdata = { ...data };

        // Convert offers to types array - send as formatted strings
        if (tdata.offers && tdata.offers.length > 0) {
            tdata.types = tdata.offers.reduce((allTypes, offer) => {
                const convertedTypes = offer.types.map(type => ({
                    id: type.id,
                    available: type.available,
                    cost: type.cost || 0,  // Send as formatted string
                    cost_coin: type.cost_coin,
                    sale: type.sale || 0,  // Send as formatted string
                    sale_coin: type.sale_coin
                }));
                return [...allTypes, ...convertedTypes];
            }, []);
            delete tdata.offers;
        } else {
            tdata.types = [];
        }

        // Ensure all boolean fields are present
        tdata.promo = tdata.promo ?? false;
        tdata.menu = tdata.menu ?? false;
        tdata.banner = tdata.banner ?? false;
        tdata.google_news = tdata.google_news ?? false;
        tdata.embed = tdata.embed ?? false;
        tdata.restrict = tdata.restrict ?? false;
        tdata.archive_article = tdata.archive_article ?? false;

        return tdata;
    }).put(route('sites.approve', props.site.id), {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Site approved successfully',
                life: 3000
            });
            // Reload the page after a short delay
            setTimeout(() => {
                window.location.reload();
            }, 1500);
        },
        onError: (errors) => {
            console.error('Approve error:', errors);
            const errorMessage = typeof errors === 'object' && errors.message
                ? errors.message
                : 'Failed to approve site';
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage,
                life: 5000
            });
        }
    });
};

const reject = () => {
    form.transform(data => {
        const tdata = { ...data };

        // Convert offers to types array - send as formatted strings
        if (tdata.offers && tdata.offers.length > 0) {
            tdata.types = tdata.offers.reduce((allTypes, offer) => {
                const convertedTypes = offer.types.map(type => ({
                    id: type.id,
                    available: type.available,
                    cost: type.cost || 0,  // Send as formatted string
                    cost_coin: type.cost_coin,
                    sale: type.sale || 0,  // Send as formatted string
                    sale_coin: type.sale_coin
                }));
                return [...allTypes, ...convertedTypes];
            }, []);
            delete tdata.offers;
        } else {
            tdata.types = [];
        }

        // Ensure all boolean fields are present
        tdata.gambling = tdata.gambling ?? false;
        tdata.sponsor = tdata.sponsor ?? false;
        tdata.promo = tdata.promo ?? false;
        tdata.menu = tdata.menu ?? false;
        tdata.banner = tdata.banner ?? false;
        tdata.google_news = tdata.google_news ?? false;
        tdata.embed = tdata.embed ?? false;
        tdata.restrict = tdata.restrict ?? false;
        tdata.archive_article = tdata.archive_article ?? false;

        return tdata;
    }).put(route('sites.reject', props.site.id), {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Site rejected successfully',
                life: 3000
            });
            // Reload the page after a short delay
            setTimeout(() => {
                window.location.reload();
            }, 1500);
        },
        onError: (errors) => {
            console.error('Reject error:', errors);
            const errorMessage = typeof errors === 'object' && errors.message
                ? errors.message
                : 'Failed to reject site';
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage,
                life: 5000
            });
        }
    });
};

const toggle = () => {
    form.post(route('sites.toggle', props.site.id), {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: props.site.status === 'DISABLED' ? 'Site reactivated successfully' : 'Site deactivated successfully',
                life: 3000
            });
        }
    });
};

const destroy = () => {
    form.delete(route('sites.destroy', props.site.id), {
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Site deleted successfully',
                life: 3000
            });
        }
    });
};

const blacklistDialog = ref(false);
const blacklistReason = ref('');
const blacklistProcessing = ref(false);

const blacklist = () => {
    blacklistProcessing.value = true;
    router.post(route('sites.blacklist', props.site.id), { reason: blacklistReason.value }, {
        preserveScroll: true,
        onSuccess: () => {
            blacklistDialog.value = false;
            blacklistReason.value = '';
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('Site blacklisted successfully'),
                life: 3000
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to blacklist site'),
                life: 3000
            });
        },
        onFinish: () => {
            blacklistProcessing.value = false;
        }
    });
};

const addPricingTableForLinkarme = () => {
    isAddingPricingTable.value = true;

    router.post(route('sites.addPricingTableForLinkarme', props.site.id), {}, {
        preserveScroll: true,
        onSuccess: (page) => {
            isAddingPricingTable.value = false;

            // Check for flash messages from backend
            const flash = page.props?.flash || {};

            if (flash.error) {
                toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: flash.error,
                    life: 5000
                });
            } else {
                toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: flash.success || 'New pricing table added successfully.',
                    life: 3000
                });

                // Reload the site data and update form.offers
                router.reload({
                    only: ['site'],
                    preserveScroll: true,
                    onSuccess: (reloadedPage) => {
                        // Update form.offers with the new data from the reloaded page
                        const newSite = reloadedPage.props.site;
                        if (newSite && newSite.offers) {
                            const processedOffers = processOffers(newSite.offers, props.types);

                            form.offers = processedOffers;

                            originalOffers.value = JSON.parse(JSON.stringify(processedOffers));

                            processedOffers.forEach(offer => {
                                if (canEditPricing(offer.seller)) {
                                    expandedTables.value.add(offer.id);
                                }
                            });
                        }
                    }
                });
            }
        },
        onError: (errors) => {
            isAddingPricingTable.value = false;
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: errors.message || 'Failed to add pricing table',
                life: 5000
            });
        }
    });
};

const addPricingTableForSeller = () => {
    if (!selectedSellerForNewPricing.value) {
        toast.add({
            severity: 'warn',
            summary: 'Warning',
            detail: 'Please select a seller first',
            life: 3000
        });
        return;
    }

    isAddingPricingTable.value = true;

    router.post(route('sites.addPricingTableForSeller', props.site.id), {
        seller_id: selectedSellerForNewPricing.value
    }, {
        preserveScroll: true,
        onSuccess: (page) => {
            isAddingPricingTable.value = false;
            selectedSellerForNewPricing.value = null;

            const flash = page.props?.flash || {};

            if (flash.error) {
                toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: flash.error,
                    life: 5000
                });
            } else {
                toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Pricing table added successfully',
                    life: 3000
                });

                router.reload({
                    only: ['site'],
                    preserveScroll: true,
                    onSuccess: (reloadedPage) => {
                        const newSite = reloadedPage.props.site;
                        if (newSite && newSite.offers) {
                            const processedOffers = processOffers(newSite.offers, props.types);

                            form.offers = processedOffers;

                            originalOffers.value = JSON.parse(JSON.stringify(processedOffers));

                            // Expand the new pricing table
                            processedOffers.forEach(offer => {
                                if (canEditPricing(offer.seller)) {
                                    expandedTables.value.add(offer.id);
                                }
                            });
                        }
                    }
                });
            }
        },
        onError: (errors) => {
            isAddingPricingTable.value = false;
            selectedSellerForNewPricing.value = null;

            console.error('Add pricing table errors:', errors);

            let errorMessage = 'Failed to add pricing table';
            if (errors && typeof errors === 'object') {
                if (errors.message) {
                    errorMessage = errors.message;
                } else if (errors.seller_id && Array.isArray(errors.seller_id)) {
                    errorMessage = errors.seller_id[0];
                }
            }

            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage,
                life: 5000
            });
        }
    });
};

const showRemovePricingConfirmation = (sellerId) => {
    sellerToRemove.value = sellerId;
    confirmRemovePricingDialog.value = true;
};

const confirmRemovePricing = () => {
    if (sellerToRemove.value === null) return;

    // Close dialog and set loading state
    confirmRemovePricingDialog.value = false;
    isRemovingPricingTable.value = true;

    const sellerIdToRemove = sellerToRemove.value;
    sellerToRemove.value = null;

    router.delete(route('sites.removePricingTable', { site: props.site.id, seller: sellerIdToRemove }), {
        preserveScroll: true,
        onSuccess: () => {
            isRemovingPricingTable.value = false;

            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Pricing table removed successfully',
                life: 3000
            });

            // Reload the site data and update form.offers
            router.reload({
                only: ['site'],
                preserveScroll: true,
                onSuccess: (reloadedPage) => {
                    const newSite = reloadedPage.props.site;
                    if (newSite && newSite.offers) {
                        const processedOffers = processOffers(newSite.offers, props.types);

                        form.offers = processedOffers;

                        originalOffers.value = JSON.parse(JSON.stringify(processedOffers));
                    }
                }
            });
        },
        onError: (errors) => {
            isRemovingPricingTable.value = false;

            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: errors.message || 'Failed to remove pricing table',
                life: 5000
            });
        }
    });
};

const toggleTableExpansion = (offerId) => {
    if (expandedTables.value.has(offerId)) {
        expandedTables.value.delete(offerId);
    } else {
        expandedTables.value.add(offerId);
    }
};

const isTableExpanded = (offerId) => {
    return expandedTables.value.has(offerId);
};

const sortedOffers = computed(() => {
  return form.offers
    ? [...form.offers].sort((a, b) => {
        return canEditPricing(a.seller) === canEditPricing(b.seller)
          ? 0
          : canEditPricing(a.seller)
          ? -1
          : 1;
      })
    : [];
});

const groupedOffers = computed(() => {
  const sorted = sortedOffers.value;
  const result = [];

  let sectionAdded = false;

  sorted.forEach((offer, index) => {
    const isLinkarme = canEditPricing(offer.seller);

    if (!isLinkarme && !sectionAdded) {
      result.push({
        type: 'section',
        title: $t('Other sellers prices'),
      });
      sectionAdded = true;
    }

    result.push({
      type: 'offer',
      data: offer,
      index,
    });
  });

  return result;
});

const availableSelectorsForNewPricing = computed(() => {
  const existingSellerIds = form.offers?.map(offer => offer.seller_id) || [];
  return props.sellers?.filter(seller =>
    !existingSellerIds.includes(seller.id) && seller.deleted_at === null
  ).map(seller => ({
    label: seller.name,
    value: seller.id
  })) || [];
});

const { formatConvertedPrice, getCurrentCurrency, onCurrencyChange } = useCurrencyFormatter();
const currentCurrency = ref(getCurrentCurrency());

onCurrencyChange(({ currency }) => {
    currentCurrency.value = currency;
});

const formattedShowcasePrice = computed(() => {
    if (!props.showcasePrice) return null;
    return formatConvertedPrice(props.showcasePrice, props.showcasePriceCoin || 'USD');
});

const getStatusSeverity = (status) => {
    switch (status) {
        case 'PENDING': return 'warn';
        case 'ENABLED': return 'success';
        case 'DISABLED': return 'danger';
        default: return 'info';
    }
};

const highestPrice = computed(() => {
    if (!form.offers?.length || !props.exchangeData) return '-';

    let highest = null;
    let highestInUSD = 0;

    for (const offer of form.offers) {
        for (const t of offer.types) {
            const rawValue = parseFloat(t.cost?.toString().replace(',', '.') || 0);
            if (isNaN(rawValue) || rawValue <= 0) continue;

            const currency = t.cost_coin?.toUpperCase() || 'USD';
            const convertedToUSD = convertCurrency(rawValue, currency, 'USD');

            if (convertedToUSD > highestInUSD) {
                highestInUSD = convertedToUSD;
                highest = { value: rawValue, currency };
            }
        }
    }

    if (!highest) return '-';

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: highest.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(highest.value);
});

// Get highest cost in USD for comparison
const highestCostInUSD = computed(() => {
    if (!form.offers?.length || !props.exchangeData) return 0;

    return Math.max(...form.offers.flatMap(offer =>
        offer.types.map(t => {
            const rawValue = parseFloat(t.cost?.toString().replace(',', '.') || 0);
            return isNaN(rawValue) || rawValue <= 0 ? 0 :
                convertCurrency(rawValue, t.cost_coin?.toUpperCase() || 'USD', 'USD');
        })
    ));
});

// Initialize original offers after form is ready
onMounted(() => {
    originalOffers.value = JSON.parse(JSON.stringify(form.offers));

    form.offers.forEach(offer => {
        if (canEditPricing(offer.seller)) {
            expandedTables.value.add(offer.id);
        }
    });
});
</script>

<template>
    <AdminMainLayout>
        <!-- Site Header -->
        <div class="grid gap-6 mb-6">
            <div class="col-12">
                <Card>
                    <template #title>
                        <div class="flex align-items-center justify-content-between w-full" style="gap: 1rem;">
                            <div class="flex items-center gap-2">
                                <i class="pi pi-pencil text-2xl"></i>
                                <span>{{ $t('Edit Site') }}: {{ site.url }}</span>
                            </div>
                            <div class="flex items-center" style="gap: 0.75rem;">
                                <Tag :value="site.formatted_status" :severity="getStatusSeverity(site.status)" class="custom-tag"/>

                                <Button v-if="site.status === 'PENDING'"
                                        :label="$t('Approve')"
                                        icon="pi pi-check"
                                        severity="success"
                                        size="small"
                                        @click="approve"
                                        :loading="form.processing" />

                                <Button v-if="site.status === 'PENDING'"
                                        :label="$t('Reject')"
                                        icon="pi pi-times"
                                        severity="danger"
                                        size="small"
                                        @click="reject"
                                        :loading="form.processing" />

                                <Button v-if="site.status === 'DISABLED'"
                                        :label="$t('Reactivate')"
                                        icon="pi pi-refresh"
                                        severity="success"
                                        size="small"
                                        @click="toggle"
                                        :loading="form.processing" />

                                <Button v-else
                                        :label="$t('Deactivate')"
                                        icon="pi pi-ban"
                                        severity="warn"
                                        size="small"
                                        @click="toggle"
                                        :loading="form.processing" />

                                <Button
                                        :label="$t('Blacklist site')"
                                        icon="pi pi-ban"
                                        severity="danger"
                                        outlined
                                        size="small"
                                        @click="blacklistReason = ''; blacklistDialog = true" />

                                <Button v-if="site.status === 'DISABLED'"
                                        :label="$t('Delete')"
                                        icon="pi pi-trash"
                                        severity="danger"
                                        size="small"
                                        @click="confirmDialog = true" />
                            </div>
                        </div>
                    </template>
                </Card>
            </div>
        </div>

        <!-- Tab View -->
        <TabView v-model:activeIndex="activeIndex">
            <!-- Site Tab -->
            <TabPanel>
                <template #header>
                    <i class="pi pi-globe mr-2"></i>
                    {{ $t('Site') }}
                </template>

                <div class="grid gap-6">
                    <!-- Note Section -->
                    <div class="col-12">
                        <Card>
                            <template #title>
                                <div class="flex items-center gap-2">
                                    <i class="pi pi-file-edit"></i>
                                    <span>{{ $t('Edit note') }}</span>
                                </div>
                            </template>
                            <template #content>
                                <form @submit.prevent="submitNote">
                                    <div class="field">
                                        <Textarea
                                            id="note"
                                            v-model="noteForm.text"
                                            rows="3"
                                            class="w-full"
                                            :placeholder="$t('Add a note about this site')"
                                        />
                                    </div>
                                    <Button type="submit"
                                        :label="$t('Save Note')"
                                        icon="pi pi-save"
                                        :loading="noteForm.processing"
                                    />
                                </form>
                            </template>
                        </Card>
                    </div>

                    <!-- Site Form -->
                    <div class="col-12">
                        <Card>
                            <template #title>
                                <div class="flex align-items-center gap-2">
                                    <i class="pi pi-cog"></i>
                                    <span>{{ $t('Site Information') }}</span>
                                </div>
                            </template>
                            <template #content>
                                <form @submit.prevent="submit">
                                    <!-- Site Form Inputs Component -->
                                    <SiteFormInputs
                                        :form="form"
                                        :categories="categories"
                                        :languages="languages"
                                        :countries="countries"
                                        :sellers="sellers"
                                        :linkOptions="linkTypeOptions"
                                        :linkLimitOptions="linksOptions"
                                        :imageLimitOptions="imagesOptions"
                                        :showUrlValidation="false"
                                        :showSeller="false"
                                        :show-entry-info="true"
                                    />

                                    <div class="flex gap-2 mt-4">
                                        <Button
                                            type="submit"
                                            :label="$t('Save')"
                                            icon="pi pi-check"
                                            :loading="form.processing" />

                                        <Button
                                            :label="$t('Cancel')"
                                            icon="pi pi-times"
                                            severity="secondary"
                                            @click="$inertia.visit(route('sites.index'))" />
                                    </div>

                                    <Message v-if="form.recentlySuccessful" severity="success" :closable="false" class="mt-3">
                                        {{ $t('Site saved successfully.') }}
                                    </Message>
                                </form>
                            </template>
                        </Card>
                    </div>
                </div>
            </TabPanel>

            <!-- Orders Tab -->
            <!-- <TabPanel>
                <template #header>
                    <i class="pi pi-shopping-cart mr-2"></i>
                    {{ $t('Orders') }}
                </template>

                <Card>
                    <template #content>
                        <DataTable :value="site.orders" paginator :rows="10" dataKey="id" class="p-datatable-sm">
                            <template #empty>
                                <div class="w-full flex flex-col items-center justify-center py-6 text-surface-500">
                                    <i class="pi pi-inbox text-4xl mb-2"></i>
                                    <span class="text-sm">{{ $t('No Orders Available') }}</span>
                                </div>
                            </template>

                            <Column field="client.name" :header="$t('Client')" sortable>
                                <template #body="slotProps">
                                        {{ slotProps.data.client.name }}
                                </template>
                            </Column>
                            <Column field="created_at" :header="$t('Date')" sortable>
                                <template #body="slotProps">
                                    {{ new Date(slotProps.data.created_at).toLocaleString() }}
                                </template>
                            </Column>
                            <Column field="status" :header="$t('Status')" sortable>
                                <template #body="slotProps">
                                    <Tag v-if="slotProps.data.status === 'COMPLETED'" severity="success">
                                        {{ slotProps.data.status }}
                                    </Tag>
                                    <Tag v-else-if="slotProps.data.status === 'PROCESSING'" severity="warn">
                                        {{ slotProps.data.status }}
                                    </Tag>
                                    <Tag v-else-if="slotProps.data.status === 'WAITING'" severity="info">
                                        {{ slotProps.data.status }}
                                    </Tag>
                                    <Tag v-else-if="slotProps.data.status === 'CANCELED'" severity="danger">
                                        {{ slotProps.data.status }}
                                    </Tag>
                                    <Tag v-else severity="info">
                                        {{ slotProps.data.status }}
                                    </Tag>
                                </template>
                            </Column>
                            <Column field="id" :header="$t('Order')" sortable>
                                <template #body="slotProps">
                                    <a :href="route('orders.index', { 'filter[search]': slotProps.data.number })" class="text-sm text-blue-600 hover:underline flex items-center">
                                        {{ $t('View Order') }}<i class="pi pi-external-link ml-1 text-xs" style="font-size: 0.8rem;"></i>
                                    </a>
                                </template>
                            </Column>
                        </DataTable>
                    </template>
                </Card>
            </TabPanel> -->

            <!-- History Tab -->
            <TabPanel>
                <template #header>
                    <i class="pi pi-history mr-2"></i>
                    {{ $t('History') }}
                </template>

                <HistoryTable
                    :cartsWithSite="cartsWithSite"
                    :ordersWithSite="ordersWithSite"
                    :liveLinksWithSite="liveLinksWithSite"
                    :offerHistory="offerHistory"
                />
            </TabPanel>

            <TabPanel>
                <template #header><i class="pi pi-dollar mr-2"></i>{{ $t('Pricing') }}</template>
                <div v-if="formattedShowcasePrice" class="flex justify-end mb-4">
                    <div class="flex items-center gap-3 p-3 border border-surface-300 rounded-lg">
                        <div class="flex items-center gap-2">
                            <span class="text-sm font-semibold text-surface-600">{{ $t('Reference Price:') }}</span>
                            <span class="text-sm font-bold text-primary">{{ formattedShowcasePrice }}</span>
                        </div>
                        <div v-if="showcaseSellerName || showcaseTypeName" class="flex items-center gap-2 pl-3 border-l border-surface-300">
                            <span v-if="showcaseSellerName" class="text-sm text-surface-600">{{ $t('Seller') }}: <Tag severity="success" :value="showcaseSellerName" class="ml-1" /></span>
                            <span v-if="showcaseTypeName" class="text-sm text-surface-600">{{ $t('Niche') }}: <Tag severity="info" :value="showcaseTypeName" class="ml-1" /></span>
                        </div>
                    </div>
                </div>

                <div class="grid gap-6">
                    <template v-for="(item, itemIndex) in groupedOffers" :key="`item-${itemIndex}`">
                        <div v-if="item.type === 'section'" class="col-12">
                            <div class="text-black px-4 py-2 rounded-lg font-semibold">{{ $t(item.title) }}</div>
                        </div>
                        <div v-else class="col-12">
                            <Card>
                                <template #title>
                                    <div class="flex align-items-center w-full gap-6">
                                        <div class="flex items-center gap-2 flex-grow-1">
                                            <i class="pi pi-dollar"></i>
                                            <span>{{ $t('Pricing table for') }} {{ item.data.seller?.name }}</span>
                                        </div>
                                        <div class="ml-auto">
                                            <Button
                                                :icon="isTableExpanded(item.data.id) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                                                severity="secondary"
                                                size="small"
                                                text
                                                @click="toggleTableExpansion(item.data.id)" />
                                        </div>
                                    </div>
                                </template>
                                <template #content>
                                    <div v-if="isTableExpanded(item.data.id)">
                                        <PricingTable
                                            :types="item.data.types"
                                            :currencyOptions="currencyOptions"
                                            :formatCurrency="formatCurrency"
                                            :isEditMode="true"
                                            :canEdit="canEditPricing(item.data.seller)"
                                            :showAvailableColumn="canEditPricing(item.data.seller) || isExternalEditMode(item.data.id)"
                                            :showRequiredMessage="canEditPricing(item.data.seller)"
                                            :exchangeData="sellerPricingData[item.data.seller_id] || exchangeData"
                                            :isLinkarme="canEditPricing(item.data.seller)"
                                            :isExternalSellerEditMode="isExternalEditMode(item.data.id)"
                                            @toggleExternalEdit="toggleExternalEditMode(item.data.id)"
                                        />
                                        <div class="flex justify-between items-center mt-4">
                                            <div class="flex gap-2">
                                                <Button :label="$t('Remove pricing table')" icon="pi pi-trash" severity="danger" size="small" outlined @click="showRemovePricingConfirmation(item.data.seller_id)" />
                                                <Button
                                                    v-if="!canEditPricing(item.data.seller)"
                                                    :label="isExternalEditMode(item.data.id) ? $t('Cancel editing') : $t('Edit external seller cost')"
                                                    :icon="isExternalEditMode(item.data.id) ? 'pi pi-times' : 'pi pi-pencil'"
                                                    :severity="isExternalEditMode(item.data.id) ? 'secondary' : 'warning'"
                                                    size="small"
                                                    @click="toggleExternalEditMode(item.data.id)"
                                                />
                                            </div>
                                            <div v-if="canEditPricing(item.data.seller) || isExternalEditMode(item.data.id)" class="flex gap-2">
                                                <Button :label="$t('Save Pricing')" icon="pi pi-check" :loading="form.processing && savingOfferId === item.data.id" @click="showPricingConfirmation(item.data.id)" />
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </Card>
                        </div>
                    </template>
                    <div class="col-12" v-if="availableSelectorsForNewPricing.length > 0 || !form.offers?.some(offer => canEditPricing(offer.seller))">
                        <!-- Show add buttons when needed -->
                        <Card>
                            <template #content>
                                <div class="flex justify-between items-center">
                                    <div>
                                        <h4 class="text-base font-semibold mb-2">{{ $t(form.offers?.length ? 'Add more pricing tables' : 'Pricing tables') }}</h4>
                                        <p class="text-sm text-surface-600">{{ $t('Add pricing tables for different sellers') }}</p>
                                    </div>
                                    <div class="flex gap-3 items-center">
                                        <!-- Add pricing table for any seller -->
                                        <div class="flex gap-2 items-center" v-if="availableSelectorsForNewPricing.length > 0">
                                            <Select
                                                v-model="selectedSellerForNewPricing"
                                                :options="availableSelectorsForNewPricing"
                                                optionLabel="label"
                                                optionValue="value"
                                                :placeholder="$t('Select seller')"
                                                class="w-40"
                                                :disabled="isAddingPricingTable"
                                            />
                                            <Button
                                                @click="addPricingTableForSeller"
                                                :label="$t('Add')"
                                                icon="pi pi-plus"
                                                severity="secondary"
                                                size="small"
                                                :disabled="isAddingPricingTable || !selectedSellerForNewPricing"
                                                :loading="isAddingPricingTable"
                                            />
                                        </div>

                                        <!-- Add Linkarme pricing table -->
                                        <Button
                                            v-if="!form.offers?.some(offer => canEditPricing(offer.seller))"
                                            @click="addPricingTableForLinkarme"
                                            :label="$t('Add Linkarme table')"
                                            icon="pi pi-plus"
                                            severity="primary"
                                            size="small"
                                            :disabled="isAddingPricingTable"
                                            :loading="isAddingPricingTable"
                                        />
                                    </div>
                                </div>
                            </template>
                        </Card>
                    </div>
                </div>
            </TabPanel>
        </TabView>

        <!-- Blacklist Confirmation Dialog -->
        <Dialog v-model:visible="blacklistDialog" modal :header="$t('Blacklist site')" :style="{ width: '95vw', maxWidth: '32rem' }" :breakpoints="{ '641px': '95vw' }">
            <div class="mb-2">
                <p class="mb-3 text-sm text-gray-600">
                    {{ $t('This will blacklist the site and remove it from the catalog.') }}
                    <span class="font-semibold">{{ site.url }}</span>
                </p>
                <label for="edit-blacklist-reason" class="font-semibold block mb-2">{{ $t('Reason (optional)') }}</label>
                <Textarea id="edit-blacklist-reason" v-model="blacklistReason" class="w-full" rows="3" :disabled="blacklistProcessing" />
            </div>
            <template #footer>
                <Button :label="$t('Cancel')" icon="pi pi-times" severity="secondary" @click="blacklistDialog = false" :disabled="blacklistProcessing" />
                <Button :label="$t('Blacklist site')" icon="pi pi-ban" severity="danger" @click="blacklist" :loading="blacklistProcessing" :disabled="blacklistProcessing" />
            </template>
        </Dialog>

        <!-- Delete Confirmation Dialog -->
        <Dialog v-model:visible="confirmDialog" modal header="Confirm Delete" :style="{width: '450px'}">
            <div class="flex align-items-center gap-3 mb-3">
                <i class="pi pi-exclamation-triangle text-orange-500" style="font-size: 2rem"></i>
                <span>{{ $t('Are you sure you want to permanently delete this site?') }}</span>
            </div>
            <template #footer>
                <Button :label="$t('Cancel')" icon="pi pi-times" severity="secondary" @click="confirmDialog = false" />
                <Button :label="$t('Delete')" icon="pi pi-check" severity="danger" @click="destroy(), confirmDialog = false" />
            </template>
        </Dialog>

        <!-- Pricing Changes Confirmation Dialog -->
        <Dialog v-model:visible="confirmPricingDialog" modal :header="$t('Confirm Pricing Update')" :style="{width: '700px'}">
            <div class="mb-4">
                <p class="mb-3">{{ $t('Review pricing before saving:') }}</p>

                <div v-for="(group, sellerName) in pricingChangesBySeller" :key="'seller-' + sellerName" class="mb-4">
                    <div v-if="editableOffers.length > 1" class="mb-2 font-semibold">
                        {{ $t('Site') }}: {{ site.url }} — {{ $t('Seller') }}: {{ sellerName }}
                    </div>
                    <DataTable :value="group" class="p-datatable-sm" stripedRows>
                        <Column :header="$t('Type')" style="width: 20%">
                            <template #body="slotProps">
                                <div class="flex align-items-center gap-2">
                                    <span class="font-semibold">{{ slotProps.data.type }}</span>
                                </div>
                            </template>
                        </Column>

                        <Column :header="$t('Cost')" style="width: 40%">
                            <template #body="slotProps">
                                <div class="flex items-center gap-2">
                                    <div v-if="slotProps.data.costChanged" class="flex align-items-center gap-2">
                                        <span class="text-color-secondary line-through">
                                            {{ new Intl.NumberFormat('en-US', { style: 'currency', currency: slotProps.data.oldCostCoin }).format(slotProps.data.oldCost) }}
                                        </span>
                                        <i class="pi pi-arrow-right text-xs"></i>
                                        <span class="font-semibold text-primary">
                                            {{ new Intl.NumberFormat('en-US', { style: 'currency', currency: slotProps.data.costCoin }).format(slotProps.data.newCost) }}
                                        </span>
                                    </div>
                                    <div v-else>
                                        {{ new Intl.NumberFormat('en-US', { style: 'currency', currency: slotProps.data.costCoin }).format(slotProps.data.newCost) }}
                                    </div>

                                    <!-- Warning icon for cost below highest price (only for Default type) -->
                                    <i
                                        v-if="slotProps.data.type === 'Default' && slotProps.data.newCost > 0 && convertCurrency(slotProps.data.newCost, slotProps.data.costCoin, 'USD') < highestCostInUSD"
                                        class="pi pi-exclamation-triangle text-orange-500 cursor-pointer"
                                        v-tooltip.top="$t('Cost is below highest price') + ': ' + highestPrice"
                                        style="font-size: 0.9rem">
                                    </i>
                                </div>
                            </template>
                        </Column>
                        <Column v-if="group.some(change => change.isLinkarme)" :header="$t('Sale')" style="width: 40%">
                            <template #body="slotProps">
                                <div class="flex items-center gap-2">
                                    <div v-if="slotProps.data.saleChanged" class="flex align-items-center gap-2">
                                        <span class="text-color-secondary line-through">
                                            {{ new Intl.NumberFormat('en-US', { style: 'currency', currency: slotProps.data.oldSaleCoin }).format(slotProps.data.oldSale) }}
                                        </span>
                                        <i class="pi pi-arrow-right text-xs"></i>
                                        <span class="font-semibold text-primary">
                                            {{ new Intl.NumberFormat('en-US', { style: 'currency', currency: slotProps.data.saleCoin }).format(slotProps.data.newSale) }}
                                        </span>
                                    </div>
                                    <div v-else>
                                        {{ new Intl.NumberFormat('en-US', { style: 'currency', currency: slotProps.data.saleCoin }).format(slotProps.data.newSale) }}
                                    </div>

                                    <!-- Warning icon for sale below suggested price -->
                                    <i
                                        v-if="slotProps.data.suggested !== '-' && slotProps.data.newSale > 0 && slotProps.data.newSale < parseFloat(slotProps.data.suggested.match(/[\d,]+\.?\d*/)?.[0]?.replace(/,/g, '') || '0')"
                                        class="pi pi-exclamation-triangle text-orange-500 cursor-pointer"
                                        v-tooltip.top="$t('Sale is below suggested price') + ': ' + slotProps.data.suggested"
                                        style="font-size: 0.9rem">
                                    </i>
                                </div>
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </div>

            <template #footer>
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    severity="secondary"
                    @click="confirmPricingDialog = false" />
                <Button
                    :label="$t('Confirm')"
                    icon="pi pi-check"
                    severity="success"
                    @click="confirmAndSavePricing"
                    :loading="form.processing" />
            </template>
        </Dialog>

        <!-- Remove Pricing Table Confirmation Dialog -->
        <Dialog v-model:visible="confirmRemovePricingDialog" modal :header="$t('Confirm Remove Pricing Table')" :style="{width: '450px'}">
            <div class="flex align-items-center gap-3 mb-3">
                <i class="pi pi-exclamation-triangle text-orange-500" style="font-size: 2rem"></i>
                <span>{{ $t('Are you sure you want to remove this pricing table? This action cannot be undone.') }}</span>
            </div>
            <template #footer>
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    severity="secondary"
                    :disabled="isRemovingPricingTable"
                    @click="confirmRemovePricingDialog = false; sellerToRemove = null" />
                <Button
                    :label="$t('Remove')"
                    icon="pi pi-trash"
                    severity="danger"
                    :loading="isRemovingPricingTable"
                    @click="confirmRemovePricing" />
            </template>
        </Dialog>
    </AdminMainLayout>
</template>

<style scoped>
.field {
    margin-bottom: 1.5rem;
}

.field label {
    display: block;
    margin-bottom: 0.5rem;
}

.custom-tag {
    font-size: 14px;
    padding: 7px 14px;
}
</style>
